import type { useInviteActions } from '@/features/team/hooks/useInviteActions';
import type { useMemberActions } from '@/features/team/hooks/useMemberActions';
import type { useRecipientActions } from '@/features/team/hooks/useRecipientActions';

/** A destructive action awaiting confirmation via ConfirmDialog. */
export type ConfirmState =
  | { kind: 'remove'; membershipId: string; name: string }
  | { kind: 'revoke'; inviteId: string; email: string }
  | { kind: 'revoke-recipient'; recipientId: string; email: string }
  | { kind: 'regenerate-recipient'; recipientId: string; email: string };

export interface ConfirmDialogConfig {
  title: string;
  description: string;
  confirmLabel: string;
  isConfirming: boolean;
  onConfirm: () => void;
}

export interface ConfirmDialogDeps {
  memberActions: ReturnType<typeof useMemberActions>;
  inviteActions: ReturnType<typeof useInviteActions>;
  recipientActions: ReturnType<typeof useRecipientActions>;
  /** Genericizes copy ("this startup" / "this investor") per entity type. */
  entityLabel: string;
  /** Closes the dialog once the mutation for the confirmed kind succeeds. */
  onDone: () => void;
}

/**
 * One place per `ConfirmState.kind` for its copy, pending state, and mutate
 * call - replaces branching the same discriminant separately across five
 * `ConfirmDialog` props (mnr-standards Style Rules: express a discriminated
 * union's per-case behavior as one lookup, not N parallel conditionals).
 */
export function getConfirmDialogConfig(
  confirm: ConfirmState | null,
  { memberActions, inviteActions, recipientActions, entityLabel, onDone }: ConfirmDialogDeps,
): ConfirmDialogConfig | null {
  if (!confirm) return null;

  switch (confirm.kind) {
    case 'remove':
      return {
        title: 'Remove team member?',
        description: `${confirm.name} will lose access to this ${entityLabel.toLowerCase()}. You can invite them again later.`,
        confirmLabel: 'Remove Member',
        isConfirming: memberActions.remove.isPending,
        onConfirm: () => memberActions.remove.mutate(confirm.membershipId, { onSuccess: onDone }),
      };
    case 'revoke':
      return {
        title: 'Remove pending invite?',
        description: `The invite to ${confirm.email} will be removed and its link will stop working. You can invite them again anytime.`,
        confirmLabel: 'Cancel invite',
        isConfirming: inviteActions.revoke.isPending,
        onConfirm: () => inviteActions.revoke.mutate(confirm.inviteId, { onSuccess: onDone }),
      };
    case 'revoke-recipient':
      return {
        title: 'Revoke access?',
        description: `${confirm.email} will immediately lose the ability to view your startup profile. You can share again later.`,
        confirmLabel: 'Revoke Access',
        isConfirming: recipientActions.remove.isPending,
        onConfirm: () => recipientActions.remove.mutate(confirm.recipientId, { onSuccess: onDone }),
      };
    case 'regenerate-recipient':
      return {
        title: 'Resend invite?',
        description: `This creates a new secure link and re-sends the invite email to ${confirm.email}. The previous link stops working immediately.`,
        confirmLabel: 'Resend Invite',
        isConfirming: recipientActions.regenerate.isPending,
        onConfirm: () => recipientActions.regenerate.mutate(confirm.recipientId, { onSuccess: onDone }),
      };
  }
}
